import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ShoppingCart, Search, Package, Check, X, Send,
  MessageSquare, ChevronDown, ChevronRight, ArrowLeft, AlertTriangle, Save, Eye, ImageOff, Upload
} from 'lucide-react';

const PRIMARY = '#0099FF';
const PRIMARY_DARK = '#0077CC';
const PRIMARY_LIGHT = 'rgba(0, 153, 255, 0.08)';
const PRIMARY_BORDER = 'rgba(0, 153, 255, 0.2)';

const ROLES = { ADMIN: 1, OPERATOR: 2, COMMERCIAL: 3, CLIENT: 5, SUPER_ADMIN: 6 };

const IVA_MAP = { 1: 4, 2: 10, 3: 21 };

const formatCurrency = (value) =>
  new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(value || 0);

const getMargin = (pvp, coste) => {
  if (!pvp || !coste || coste <= 0 || pvp <= 0) return null;
  return ((pvp - coste) / pvp) * 100;
};

const getPriceBgClass = (pvp, coste) => {
  if (!pvp || !coste || coste <= 0) return '';
  // Igual o por debajo del coste -> rojo (margen cero o negativo).
  if (pvp <= coste) return 'bg-red-50 text-red-700';
  const margin = getMargin(pvp, coste);
  if (margin !== null && margin < 30) return 'bg-yellow-50 text-yellow-700';
  return 'bg-green-50 text-green-700';
};

const isAdmin = (permisos) => [ROLES.ADMIN, ROLES.OPERATOR, ROLES.SUPER_ADMIN].includes(permisos);
const isCommercial = (permisos) => permisos === ROLES.COMMERCIAL;
const isClient = (permisos) => permisos === ROLES.CLIENT;

// ── Client Search Dropdown ──
const ClientSearchDropdown = ({ selectedClient, onSelect, token, API_URL, showId }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searching, setSearching] = useState(false);
  const timeoutRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const doSearch = useCallback(async (term) => {
    if (!term || term.length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const res = await fetch(`${API_URL}/clients/search?q=${encodeURIComponent(term)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setResults(data.clients || data || []);
      }
    } catch (err) {
      console.error('Error searching clients:', err);
    } finally {
      setSearching(false);
    }
  }, [API_URL, token]);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    setShowDropdown(true);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => doSearch(val), 300);
  };

  if (selectedClient) {
    return (
      <div className="flex items-center gap-3 px-4 py-2 rounded-lg border" style={{ borderColor: PRIMARY_BORDER, backgroundColor: PRIMARY_LIGHT }}>
        <Package size={16} style={{ color: PRIMARY }} />
        <span className="text-sm font-medium text-gray-800">
          {selectedClient.razon_social || selectedClient.alias}
        </span>
        {showId && <span className="text-xs text-gray-500">#{selectedClient.id}</span>}
        <button onClick={() => onSelect(null)} className="ml-auto text-gray-400 hover:text-red-500">
          <X size={16} />
        </button>
      </div>
    );
  }

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={query}
          onChange={handleInputChange}
          onFocus={() => results.length > 0 && setShowDropdown(true)}
          placeholder="Buscar cliente por nombre, CIF o alias..."
          className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-transparent"
          style={{ '--tw-ring-color': PRIMARY }}
        />
        {searching && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2" style={{ borderColor: PRIMARY }} />
          </div>
        )}
      </div>
      {showDropdown && results.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {results.map((client) => (
            <button
              key={client.id}
              onClick={() => { onSelect(client); setShowDropdown(false); setQuery(''); }}
              className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 transition-colors"
            >
              <div className="text-sm font-medium text-gray-800">{client.razon_social}</div>
              <div className="text-xs text-gray-500">{client.alias} &middot; {client.cif}{showId ? <> &middot; #{client.id}</> : ''}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Pre-Confirmation Modal ──
const PreConfirmModal = ({ isOpen, onClose, orderLines, familiesData, onConfirm, loading, confirmError }) => {
  const [npedidoc, setNpedidoc] = useState('');

  // Build a lookup map: idarticulo -> product data from familiesData
  const productMap = useMemo(() => {
    const map = {};
    (familiesData || []).forEach((family) => {
      (family.products || []).forEach((p) => {
        const pid = p.id || p.idarticulo;
        map[pid] = p;
      });
    });
    return map;
  }, [familiesData]);

  // Find lines with embalaje issues
  const embalajeIssues = useMemo(() => {
    return (orderLines || []).filter((line) => {
      const product = productMap[line.idarticulo];
      const emb = product?.cantidad_embalaje || product?.embalaje || 1;
      const qty = line.cantidad || 0;
      return emb > 1 && qty % emb !== 0;
    }).map((line) => {
      const product = productMap[line.idarticulo];
      const emb = product?.cantidad_embalaje || product?.embalaje || 1;
      const qty = line.cantidad || 0;
      const adjusted = Math.ceil(qty / emb) * emb;
      return {
        nombre: line.nombre || line.descripcion || product?.nombre || `Art. ${line.idarticulo}`,
        cantidad: qty,
        embalaje: emb,
        cantidad_ajustada: adjusted
      };
    });
  }, [orderLines, productMap]);

  const hasIssues = embalajeIssues.length > 0;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 overflow-hidden flex flex-col max-h-[85vh]">
        {/* Header */}
        <div className="px-6 py-4 border-l-4 border-blue-500 bg-white flex items-center justify-between flex-shrink-0">
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Check size={20} style={{ color: PRIMARY }} /> Confirmar Pedido
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6 min-h-0">
          {/* Custom Order Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              N&ordm; Pedido del cliente (opcional)
            </label>
            <input
              type="text"
              value={npedidoc}
              onChange={(e) => setNpedidoc(e.target.value)}
              placeholder="Ej: PED-2024-001 o dejar vacío para S/N"
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-transparent"
              style={{ '--tw-ring-color': PRIMARY }}
            />
          </div>

          {/* Embalaje Verification */}
          <div>
            <h4 className="text-sm font-semibold text-gray-800 mb-3">Verificaci&oacute;n de Embalajes</h4>
            {hasIssues ? (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Producto</th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Cantidad Actual</th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Embalaje</th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Cantidad Ajustada</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {embalajeIssues.map((item, idx) => (
                      <tr key={idx} className="bg-yellow-50">
                        <td className="px-3 py-2 text-sm text-gray-800">{item.nombre}</td>
                        <td className="px-3 py-2 text-sm text-center text-gray-600">{item.cantidad}</td>
                        <td className="px-3 py-2 text-sm text-center text-gray-600">{item.embalaje}</td>
                        <td className="px-3 py-2 text-sm text-center font-medium text-amber-700">{item.cantidad_ajustada}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
                <span className="text-lg">&#10003;</span> Todos los productos cumplen con el embalaje
              </div>
            )}
          </div>

          {/* Error inside modal */}
          {confirmError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
              <AlertTriangle size={16} />
              {confirmError}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center gap-3 justify-end flex-shrink-0">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
          {hasIssues ? (
            <>
              <button
                onClick={() => onConfirm('original', npedidoc)}
                disabled={loading}
                className="px-4 py-2 text-sm text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 font-medium"
              >
                Confirmar Pedido Original
              </button>
              <button
                onClick={() => onConfirm('embalaje', npedidoc)}
                disabled={loading}
                className="px-4 py-2 text-sm text-white rounded-lg transition-colors disabled:opacity-50 font-medium flex items-center gap-1.5"
                style={{ backgroundColor: PRIMARY }}
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                ) : (
                  <Check size={16} />
                )}
                Confirmar con Embalaje Optimizado
              </button>
            </>
          ) : (
            <button
              onClick={() => onConfirm('original', npedidoc)}
              disabled={loading}
              className="px-4 py-2 text-sm text-white rounded-lg transition-colors disabled:opacity-50 font-medium flex items-center gap-1.5"
              style={{ backgroundColor: PRIMARY }}
            >
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              ) : (
                <Check size={16} />
              )}
              Confirmar Pedido
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Comments Modal ──
const CommentsModal = ({ isOpen, onClose, orderId, token, API_URL }) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchComments = useCallback(async () => {
    if (!orderId) return;
    try {
      const res = await fetch(`${API_URL}/orders/${orderId}/comentarios`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setComments(data.comentarios || data || []);
      }
    } catch (err) { console.error(err); }
  }, [orderId, API_URL, token]);

  useEffect(() => {
    if (isOpen && orderId) fetchComments();
  }, [isOpen, orderId, fetchComments]);

  const handleSubmit = async () => {
    if (!newComment.trim() || !orderId) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/orders/${orderId}/comentarios`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ texto: newComment.trim() })
      });
      if (res.ok) {
        setNewComment('');
        fetchComments();
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden flex flex-col max-h-[80vh]">
        <div className="px-6 py-4 text-white flex items-center justify-between flex-shrink-0" style={{ backgroundColor: PRIMARY }}>
          <h3 className="text-lg font-bold flex items-center gap-2">
            <MessageSquare size={20} /> Comentarios
          </h3>
          <button onClick={onClose} className="text-white/80 hover:text-white">
            <X size={20} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-0">
          {comments.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-8">No hay comentarios</p>
          ) : (
            comments.map((c, i) => (
              <div key={c.id || i} className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-semibold text-gray-700">{c.usuario || 'Sistema'}</span>
                  <span className="text-xs text-gray-400">
                    {c.fecha ? new Date(c.fecha).toLocaleString('es-ES') : ''}
                  </span>
                </div>
                {/* La API devuelve el texto en `comentario` (columna real). Se
                    deja `texto` de reserva por compatibilidad. */}
                <p className="text-sm text-gray-600">{c.comentario || c.texto}</p>
              </div>
            ))
          )}
        </div>
        <div className="p-4 border-t border-gray-200 flex gap-2 flex-shrink-0">
          <input
            type="text"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
            placeholder="Escribe un comentario..."
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2"
            style={{ '--tw-ring-color': PRIMARY }}
          />
          <button
            onClick={handleSubmit}
            disabled={loading || !newComment.trim()}
            className="px-4 py-2 text-white rounded-lg text-sm font-medium disabled:opacity-50 transition-colors"
            style={{ backgroundColor: PRIMARY }}
          >
            <Send size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Image Popup ──
const ImagePopup = ({ src, onClose }) => {
  if (!src) return null;
  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60"
      onClick={onClose}
    >
      <div className="relative" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onClose}
          className="absolute -top-3 -right-3 bg-white rounded-full p-1 shadow-lg text-gray-500 hover:text-gray-800 z-10"
        >
          <X size={18} />
        </button>
        <img
          src={src}
          alt="Producto"
          className="rounded-lg shadow-2xl"
          style={{ maxWidth: '500px', maxHeight: '80vh', objectFit: 'contain' }}
          onError={(e) => {
            // Mismo fallback que las miniaturas: probar .png -> .jpg -> .jpeg -> .gif
            // si la extension inicial falla. Replica class_producto.php (PHP legacy).
            const currentSrc = e.target.src;
            const baseUrl = currentSrc.replace(/\.[^./]+$/, '');
            const tried = e.target.dataset.tried ? e.target.dataset.tried.split(',') : [];
            const exts = ['.png', '.jpg', '.jpeg', '.gif'].filter(x => !tried.includes(x));
            if (exts.length === 0) return;
            const nextExt = exts[0];
            e.target.dataset.tried = [...tried, nextExt].join(',');
            e.target.src = `${baseUrl}${nextExt}`;
          }}
        />
      </div>
    </div>
  );
};

// ── Product Row ──
const ProductRow = ({
  product,
  permisos,
  quantity,
  onQuantityChange,
  onQuantityCommit,
  onPriceChange,
  onPriceCommit,
  flashId,
  priceConfirmId,
  readOnly,
  productImages,
  onImageClick,
  imageBaseUrl
}) => {
  const pid = product.id || product.idarticulo;
  const coste = parseFloat(product.precio_coste) || 0;
  const pvp = parseFloat(product.effective_price) || parseFloat(product.client_price) || parseFloat(product.pvp) || parseFloat(product.precio) || 0;
  const iva = IVA_MAP[product.iva || product.tipo_iva] || 21;
  const emb = product.cantidad_embalaje || product.embalaje || 1;
  const isFlashing = flashId === pid;
  const showPriceConfirm = priceConfirmId === pid;

  const qtyVal = quantity !== undefined && quantity !== null ? quantity : '';
  const qtyNum = parseInt(qtyVal, 10) || 0;
  const embWarning = emb > 1 && qtyNum > 0 && qtyNum % emb !== 0;

  const handleQtyKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  };

  const handlePriceKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  };

  // Estética del PHP refactorizada: los productos con precio asignado al
  // cliente (has_price) muestran un fondo azul suave; los demás (que solo
  // ve el admin) salen blancos. El backend ya ordena con asignados primero.
  // Tono escogido: azul muy claro, mucho menos estridente que el w3-blue
  // original del PHP. Hover/flash mantienen prioridad visual.
  const hasAssignedPrice = product.has_price === true || (product.client_price != null && product.client_price !== undefined);
  const baseRowBg = hasAssignedPrice ? 'bg-sky-50' : 'bg-white';
  const hoverBg = hasAssignedPrice ? 'hover:bg-sky-100' : 'hover:bg-gray-50';
  const flashBg = isFlashing ? 'bg-green-100' : '';

  // Un producto "No asignado" (sin precio de venta) NO se puede pedir: cliente y
  // comercial lo ven pero tienen la cantidad bloqueada. Solo el admin, que puede
  // teclearle un precio, la tiene habilitada. El backend ademas rechaza anadir
  // un producto sin precio, asi que esto es la barrera de UX equivalente.
  const canOrder = hasAssignedPrice || isAdmin(permisos);

  return (
    <tr className={`${flashBg || `${baseRowBg} ${hoverBg}`} transition-all duration-300`}>
      <td className="px-1 py-1.5 w-[50px]">
        {productImages && productImages[pid] ? (
          <img
            src={`${imageBaseUrl}${productImages[pid]}`}
            alt=""
            className="w-10 h-10 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => onImageClick && onImageClick(`${imageBaseUrl}${productImages[pid]}`)}
            onError={(e) => {
              // Fallback como el PHP legacy (class_producto.php): probar
              // .png → .jpg → .jpeg → .gif si la extensión guardada falla.
              const currentSrc = e.target.src;
              const baseUrl = currentSrc.replace(/\.[^./]+$/, '');
              const tried = e.target.dataset.tried ? e.target.dataset.tried.split(',') : [];
              const exts = ['.png', '.jpg', '.jpeg', '.gif'].filter(x => !tried.includes(x));
              if (exts.length === 0) { e.target.style.display = 'none'; return; }
              const nextExt = exts[0];
              e.target.dataset.tried = [...tried, nextExt].join(',');
              e.target.src = `${baseUrl}${nextExt}`;
            }}
          />
        ) : (
          <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center">
            <ImageOff size={16} className="text-gray-300" />
          </div>
        )}
      </td>
      <td className="px-3 py-1.5 text-xs text-gray-500 font-mono whitespace-nowrap">
        {product.referencia || product.raiz || pid}
      </td>
      <td className="px-3 py-1.5 text-sm text-gray-800 max-w-[360px] whitespace-normal break-words">
        {product.nombre || product.descripcion}
      </td>

      {/* P.Coste - only admin */}
      {isAdmin(permisos) && (
        <td className="px-3 py-1.5 text-sm text-right text-gray-400">
          {formatCurrency(coste)}
        </td>
      )}

      {/* PVP / Precio */}
      {isAdmin(permisos) ? (
        <td className="px-3 py-1.5">
          <div className="flex items-center justify-end gap-1">
            {/* Sin precio de venta asignado, el campo va vacío con el aviso
                "No asignado" (antes mostraba el coste como valor). El admin
                puede escribir el precio igualmente. */}
            <input
              type="number"
              step="any"
              min="0"
              defaultValue={hasAssignedPrice ? pvp : ''}
              placeholder={hasAssignedPrice ? '' : 'No asignado'}
              disabled={readOnly}
              onFocus={(e) => e.target.select()}
              onBlur={(e) => {
                const newVal = parseFloat(e.target.value);
                if (!isNaN(newVal) && newVal !== pvp) {
                  onPriceChange(product, newVal);
                }
              }}
              onKeyDown={handlePriceKeyDown}
              className={`w-24 px-2 py-1 border-2 border-blue-400 rounded text-sm text-right focus:outline-none focus:ring-1 focus:ring-blue-500 ${readOnly ? 'bg-gray-100 cursor-not-allowed' : ''} ${hasAssignedPrice ? getPriceBgClass(parseFloat(pvp), coste) : 'placeholder:text-gray-400 placeholder:italic placeholder:text-xs'}`}
            />
            {showPriceConfirm && (
              <Check size={14} className="text-green-500 flex-shrink-0" />
            )}
          </div>
        </td>
      ) : (
        <td className={`px-3 py-1.5 text-sm text-right ${product.tiene_precio_custom ? 'text-blue-600 font-medium' : 'text-gray-700'}`}>
          {/* El cliente solo ve el precio de VENTA si lo tiene asignado. Sin
              precio de venta asignado se indica "No asignado" (no se muestra el
              coste ni un 0). */}
          {hasAssignedPrice
            ? formatCurrency(pvp)
            : <span className="text-gray-400 italic text-xs">No asignado</span>}
        </td>
      )}

      {/* IVA */}
      <td className="px-3 py-1.5 text-xs text-center text-gray-500">
        {iva}%
      </td>

      {/* Embalaje */}
      <td className="px-3 py-1.5 text-sm text-center text-gray-600">
        {emb}
      </td>

      {/* Cantidad */}
      <td className="px-3 py-1.5">
        <div className="flex flex-col items-center">
          <input
            type="number"
            min="0"
            value={canOrder ? qtyVal : ''}
            disabled={readOnly || !canOrder}
            onChange={(e) => onQuantityChange(pid, e.target.value)}
            onBlur={() => onQuantityCommit(product)}
            onKeyDown={handleQtyKeyDown}
            className={`w-16 px-2 py-1 border border-gray-300 rounded text-sm text-center focus:outline-none focus:ring-1 focus:ring-blue-400 ${(readOnly || !canOrder) ? 'bg-gray-100 cursor-not-allowed' : ''}`}
            placeholder={canOrder ? '0' : '—'}
            title={canOrder ? '' : 'Producto sin precio asignado: no se puede pedir'}
          />
          {!canOrder && (
            <span className="text-[10px] text-gray-400 italic mt-0.5">Sin precio</span>
          )}
          {embWarning && (
            <span className="text-[10px] text-yellow-600 mt-0.5">Emb: {emb}</span>
          )}
        </div>
      </td>
    </tr>
  );
};

// ── Family Accordion ──
const FamilySection = ({
  family,
  products,
  expanded,
  onToggle,
  permisos,
  quantities,
  onQuantityChange,
  onQuantityCommit,
  onPriceChange,
  flashId,
  priceConfirmId,
  readOnly,
  productImages,
  onImageClick,
  imageBaseUrl,
  hideHeader
}) => {
  return (
    <div className="border-b border-gray-200 last:border-b-0">
      {/* Family Header — se oculta para clientes (piden lista sin familias) */}
      {!hideHeader && (
        <button
          onClick={onToggle}
          className="w-full flex items-center justify-between px-4 py-2.5 text-white font-bold text-sm"
          style={{ backgroundColor: '#6B7280' }}
        >
          <div className="flex items-center gap-2">
            {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            <span>{family}</span>
          </div>
          <span className="text-xs font-normal bg-white/20 px-2 py-0.5 rounded">
            {products.length} producto{products.length !== 1 ? 's' : ''}
          </span>
        </button>
      )}

      {/* Products Table */}
      {(hideHeader || expanded) && products.length > 0 && (
        <table className="min-w-full divide-y divide-gray-100">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-1 py-1.5 text-center text-xs font-medium text-gray-500 uppercase w-[50px]"></th>
              <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">Ref</th>
              <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">Producto</th>
              {isAdmin(permisos) && (
                <th className="px-3 py-1.5 text-right text-xs font-medium text-gray-500 uppercase">P.Coste</th>
              )}
              <th className="px-3 py-1.5 text-right text-xs font-medium text-gray-500 uppercase">
                {isClient(permisos) ? 'Precio' : 'PVP'}
              </th>
              <th className="px-3 py-1.5 text-center text-xs font-medium text-gray-500 uppercase">IVA</th>
              <th className="px-3 py-1.5 text-center text-xs font-medium text-gray-500 uppercase">Emb.</th>
              <th className="px-3 py-1.5 text-center text-xs font-medium text-gray-500 uppercase w-24">Cantidad</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {products.map((product) => {
              const pid = product.id || product.idarticulo;
              return (
                <ProductRow
                  key={pid}
                  product={product}
                  permisos={permisos}
                  quantity={quantities[pid]}
                  onQuantityChange={onQuantityChange}
                  onQuantityCommit={onQuantityCommit}
                  onPriceChange={onPriceChange}
                  onPriceCommit={() => {}}
                  flashId={flashId}
                  priceConfirmId={priceConfirmId}
                  readOnly={readOnly}
                  productImages={productImages}
                  onImageClick={onImageClick}
                  imageBaseUrl={imageBaseUrl}
                />
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

// ── Main Component ──
const NuevoPedido = () => {
  const { token, API_URL, user, activeClient } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const permisos = user?.permisos;

  // URL params for edit/view mode
  const urlOrderId = searchParams.get('order');
  const urlReadOnly = searchParams.get('readonly') === '1';

  // State
  const [selectedClient, setSelectedClient] = useState(null);
  const [orderId, setOrderId] = useState(null);
  // #4: importar pedido desde Excel (2 columnas: codigo, cantidad)
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null); // {added, sinPrecio:[], noEncontrado:[]}
  const [precioInputs, setPrecioInputs] = useState({}); // precio tecleado por idarticulo en el modal
  const importFileRef = useRef(null);

  const handleImportExcel = async (e) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = ''; // permitir re-subir el mismo archivo
    if (!file || !orderId) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('archivo', file);
      const res = await fetch(`${API_URL}/orders/${orderId}/import-excel`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Error al importar el Excel');
      setImportResult(data);
      fetchOrderLines();
    } catch (err) {
      setImportResult({ error: err.message });
    } finally {
      setImporting(false);
    }
  };

  // Asignar precio a un producto sin precio (desde el resultado del import) y anadirlo al pedido
  const assignAndAddFromImport = async (item, precioStr) => {
    const precio = parseFloat(precioStr);
    if (isNaN(precio) || precio < 0 || !selectedClient || !orderId) return;
    try {
      const cmatriz = selectedClient.clinica_matriz || selectedClient.id;
      const r1 = await fetch(`${API_URL}/prices`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ idarticulo: item.idarticulo, cmatriz, precio }),
      });
      if (!r1.ok) throw new Error('No se pudo asignar el precio');
      const r2 = await fetch(`${API_URL}/orders/${orderId}/lineas`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ idarticulo: item.idarticulo, cantidad: item.cantidad, precio }),
      });
      if (!r2.ok) throw new Error('No se pudo anadir al pedido');
      setImportResult((prev) => prev ? {
        ...prev,
        added: (prev.added || 0) + 1,
        sinPrecio: (prev.sinPrecio || []).filter((x) => x.idarticulo !== item.idarticulo),
      } : prev);
      fetchOrderLines();
    } catch (err) {
      setImportResult((prev) => (prev ? { ...prev, error: err.message } : prev));
    }
  };
  const [orderLines, setOrderLines] = useState([]);
  // Lineas del detalle cuyo nombre completo esta desplegado (boton "ver mas").
  const [expandedLines, setExpandedLines] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [familiesData, setFamiliesData] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [showCommentsModal, setShowCommentsModal] = useState(false);
  const [showPreConfirmModal, setShowPreConfirmModal] = useState(false);
  const [confirmError, setConfirmError] = useState('');
  const [commentCount, setCommentCount] = useState(0);
  const [quantities, setQuantities] = useState({});
  const [prevQuantities, setPrevQuantities] = useState({});
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  // Aviso (no bloqueante) cuando el admin mete un producto cuyo precio esta
  // igual o por debajo del precio coste. El producto SE permite igual.
  const [priceWarning, setPriceWarning] = useState('');
  const [flashId, setFlashId] = useState(null);
  const [priceConfirmId, setPriceConfirmId] = useState(null);
  const [expandedFamilies, setExpandedFamilies] = useState({});
  const [saving, setSaving] = useState(false);
  const [orderEstado, setOrderEstado] = useState(null);
  const [orderNpedido, setOrderNpedido] = useState(null);
  const [loadingOrder, setLoadingOrder] = useState(!!urlOrderId);
  const [productImages, setProductImages] = useState({});
  const [selectedImage, setSelectedImage] = useState(null);
  const [showOpenOrderModal, setShowOpenOrderModal] = useState(false);
  const [openOrderId, setOpenOrderId] = useState(null);
  const [pendingClient, setPendingClient] = useState(null);

  // Images now come as full URLs from the backend
  const imageBaseUrl = '';

  // Derived: is this truly read-only?
  // Un pedido confirmado (estado != 1) es solo-lectura para cliente/comercial,
  // pero el ADMIN SI puede modificar lineas/cantidades de pedidos confirmados.
  // El modo "ver" explicito (?readonly=1) siempre gana, tambien para admin.
  const readOnly = urlReadOnly || (orderEstado !== null && orderEstado !== 1 && !isAdmin(permisos));
  const isEditingExisting = !!urlOrderId;
  // Admin editando un pedido YA confirmado (estado != 1). Las ediciones de
  // linea se guardan solas al confirmar la cantidad, asi que NO se muestran los
  // botones "Confirmar Pedido" / "Guardar Borrador": evitan reenviar el correo
  // al cliente y regresar el estado del pedido por accidente.
  const editingConfirmed = isEditingExisting && orderEstado !== null && orderEstado !== 1;

  // Numero de pedido a mostrar: el npedido de la BD (= id si no es custom).
  // Nunca mostramos "S/N": si quedo asi por un pedido legacy, caemos al id.
  const displayNpedido = (orderNpedido && orderNpedido !== 'S/N') ? orderNpedido : orderId;

  // Load existing order from URL param ?order=X
  useEffect(() => {
    if (!urlOrderId) return;
    const loadExistingOrder = async () => {
      setLoadingOrder(true);
      try {
        const res = await fetch(`${API_URL}/orders/${urlOrderId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          const order = data.order || data;
          setOrderId(parseInt(urlOrderId, 10));
          setOrderEstado(order.estado);
          setOrderNpedido(order.npedido || urlOrderId);

          // Load client info from order
          const clienteId = order.cliente || order.idcliente;
          if (clienteId) {
            try {
              const clientRes = await fetch(`${API_URL}/clients/${clienteId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
              });
              if (clientRes.ok) {
                const clientData = await clientRes.json();
                setSelectedClient(clientData.client || clientData);
              }
            } catch (err) {
              console.error('Error loading client for order:', err);
              // Fallback: set minimal client info
              setSelectedClient({ id: clienteId, razon_social: order.razon_social || `Cliente #${clienteId}` });
            }
          }

          // Pre-fill order lines and quantities
          const lines = order.lineas || data.lineas || [];
          setOrderLines(lines);
          const qtyMap = {};
          lines.forEach((line) => {
            qtyMap[line.idarticulo] = line.cantidad;
          });
          setQuantities(qtyMap);
          setPrevQuantities({ ...qtyMap });
          // El backend no devuelve `comentarios_count`; manda el array
          // `comentarios` en el detalle. Contamos sobre el array.
          setCommentCount((order.comentarios || data.comentarios || []).length);
        } else {
          setError('No se pudo cargar el pedido');
        }
      } catch (err) {
        console.error('Error loading order:', err);
        setError('Error al cargar el pedido');
      } finally {
        setLoadingOrder(false);
      }
    };
    loadExistingOrder();
  }, [urlOrderId, API_URL, token]);

  // Pre-select client from URL param
  useEffect(() => {
    const clienteId = searchParams.get('cliente');
    if (clienteId && !selectedClient && !urlOrderId) {
      const fetchClient = async () => {
        try {
          const res = await fetch(`${API_URL}/clients/${clienteId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (res.ok) {
            const data = await res.json();
            setSelectedClient(data.client || data);
          }
        } catch (err) { console.error(err); }
      };
      fetchClient();
    }
  }, [searchParams, API_URL, token, selectedClient]);

  // For CLIENT role, auto-select the active client (from Header selector if
  // they have multiple clinics, or their own clinica_matriz otherwise).
  // Si cambian el cliente activo desde el Header, se re-sincroniza aquí.
  useEffect(() => {
    if (!isClient(permisos)) return;
    // Priorizar el activeClient del context (selector global del Header).
    // Si no hay activeClient aún (puede tardar un tick en cargar la jerarquía),
    // fallback al user.cliente del JWT.
    const target = activeClient
      ? { id: activeClient.id, razon_social: activeClient.razon_social || activeClient.alias || 'Mi cuenta' }
      : (user?.cliente ? { id: user.cliente, razon_social: user.nombrec || 'Mi cuenta' } : null);
    if (target && (!selectedClient || selectedClient.id !== target.id)) {
      setSelectedClient(target);
    }
  }, [permisos, user, activeClient, selectedClient]);

  // Check for open orders and create order when client is selected (only for NEW orders)
  useEffect(() => {
    if (selectedClient && !orderId && !isEditingExisting) {
      const checkAndCreateOrder = async () => {
        try {
          // Check if there's already an open order for this client+user
          const checkRes = await fetch(
            `${API_URL}/orders/check-open?cliente=${selectedClient.id}`,
            { headers: { 'Authorization': `Bearer ${token}` } }
          );
          if (checkRes.ok) {
            const checkData = await checkRes.json();
            if (checkData.hasOpen) {
              // Show modal asking user what to do
              setOpenOrderId(checkData.orderId);
              setPendingClient(selectedClient);
              setShowOpenOrderModal(true);
              return;
            }
          }

          // No open order, create new one
          const res = await fetch(`${API_URL}/orders`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ cliente: selectedClient.id })
          });
          if (res.ok) {
            const data = await res.json();
            setOrderId(data.id || data.order?.id);
            // El npedido se asigna en el backend (= id del pedido si no se
            // indica uno custom). Lo guardamos para mostrarlo en vez de S/N.
            if (data.npedido) setOrderNpedido(data.npedido);
          }
        } catch (err) {
          console.error('Error creating order:', err);
          setError('Error al crear el pedido');
        }
      };
      checkAndCreateOrder();
    }
  }, [selectedClient, orderId, isEditingExisting, API_URL, token]);

  // Load products by family when client selected (or when admin searches)
  const loadProducts = useCallback(async (clientId, searchVal) => {
    if (!clientId) return;
    setProductsLoading(true);
    try {
      const params = new URLSearchParams({ cliente: clientId });
      if (searchVal && searchVal.trim().length >= 2) params.set('search', searchVal.trim());
      const res = await fetch(
        `${API_URL}/orders/products-by-family?${params}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        const families = data.families || [];
        setFamiliesData(families);
        // Expand all families by default
        const expanded = {};
        families.forEach((f) => { expanded[f.id || f.nombre || 'SIN FAMILIA'] = true; });
        setExpandedFamilies(expanded);
      }
    } catch (err) {
      console.error('Error loading products:', err);
    } finally {
      setProductsLoading(false);
    }
  }, [API_URL, token]);

  // For client: load all products when client selected
  // For admin: don't load until search
  useEffect(() => {
    if (selectedClient && !isAdmin(permisos)) {
      loadProducts(selectedClient.id);
    }
  }, [selectedClient, loadProducts, permisos]);

  // Admin: search triggers backend fetch with debounce
  const searchTimerRef = useRef(null);
  useEffect(() => {
    if (!isAdmin(permisos) || !selectedClient) return;
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (!searchTerm || searchTerm.trim().length < 2) {
      setFamiliesData([]);
      return;
    }
    searchTimerRef.current = setTimeout(() => {
      loadProducts(selectedClient.id, searchTerm);
    }, 400);
    return () => clearTimeout(searchTimerRef.current);
  }, [searchTerm, selectedClient, permisos, loadProducts]);

  // Fetch product images in bulk when familiesData changes
  useEffect(() => {
    if (!familiesData.length) return;
    const allIds = [];
    familiesData.forEach((family) => {
      (family.products || []).forEach((p) => {
        const pid = p.id || p.idarticulo;
        if (pid) allIds.push(pid);
      });
    });
    if (allIds.length === 0) return;

    const fetchImages = async () => {
      try {
        const res = await fetch(`${API_URL}/catalogo/productos/imagenes`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ ids: allIds })
        });
        if (res.ok) {
          const data = await res.json();
          setProductImages(data.images || {});
        }
      } catch (err) {
        console.error('Error fetching product images:', err);
      }
    };
    fetchImages();
  }, [familiesData, API_URL, token]);

  // Fetch order lines
  const fetchOrderLines = useCallback(async () => {
    if (!orderId) return;
    try {
      const res = await fetch(`${API_URL}/orders/${orderId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        const lines = data.lineas || data.order?.lineas || [];
        setOrderLines(lines);
        setCommentCount((data.order?.comentarios || data.comentarios || []).length);
        // Pre-fill quantities from existing order lines
        const qtyMap = {};
        lines.forEach((line) => {
          qtyMap[line.idarticulo] = line.cantidad;
        });
        setQuantities((prev) => {
          const merged = { ...prev };
          // Set quantities for products in order, but don't override user's current edits
          Object.keys(qtyMap).forEach((artId) => {
            merged[artId] = qtyMap[artId];
          });
          // Depurar refs que ya no estan en el pedido (cantidad puesta a 0).
          // OJO: el valor local puede ser el STRING "0" (lo que escribe el
          // usuario en el input), y "0" === 0 es false, por lo que antes NO se
          // depuraba y el input quedaba pegado en 0. Comparamos como numero.
          Object.keys(merged).forEach((artId) => {
            if (!(artId in qtyMap) && (parseInt(merged[artId], 10) || 0) === 0) {
              delete merged[artId];
            }
          });
          return merged;
        });
        setPrevQuantities((prev) => {
          const merged = { ...prev };
          Object.keys(qtyMap).forEach((artId) => {
            merged[artId] = qtyMap[artId];
          });
          // prevQuantities refleja la cantidad confirmada en servidor. Si una
          // ref ya no esta en el pedido (eliminada), hay que quitarla; si no,
          // conservaba la cantidad vieja y al re-meter la MISMA cantidad el
          // guard `qty === prevQty` del commit bloqueaba el re-alta.
          Object.keys(merged).forEach((artId) => {
            if (!(artId in qtyMap)) delete merged[artId];
          });
          return merged;
        });
      }
    } catch (err) { console.error(err); }
  }, [orderId, API_URL, token]);

  useEffect(() => {
    if (orderId) fetchOrderLines();
  }, [orderId, fetchOrderLines]);

  // Client-side filtering (only for non-admin — admin searches server-side)
  const filteredFamilies = useMemo(() => {
    if (!familiesData.length) return [];
    // Admin: backend already filtered, no client-side filter needed
    if (isAdmin(permisos)) return familiesData;

    const term = searchTerm.toLowerCase().trim();
    return familiesData
      .map((family) => {
        if (!term) return family;
        const filtered = (family.products || []).filter((p) => {
          const nombre = (p.nombre || p.descripcion || '').toLowerCase();
          const raiz = (p.raiz || p.referencia || '').toLowerCase();
          return nombre.includes(term) || raiz.includes(term);
        });
        return { ...family, products: filtered };
      })
      .filter((family) => !term || family.products.length > 0);
  }, [familiesData, searchTerm, permisos]);

  // Sort families: named families first, SIN FAMILIA at bottom
  const sortedFamilies = useMemo(() => {
    const withName = [];
    const sinFamilia = [];
    filteredFamilies.forEach((f) => {
      const name = (f.nombre || '').toUpperCase();
      if (!name || name === 'SIN FAMILIA' || name === 'SIN_FAMILIA') {
        sinFamilia.push(f);
      } else {
        withName.push(f);
      }
    });
    return [...withName, ...sinFamilia];
  }, [filteredFamilies]);

  // Toggle family expansion
  const toggleFamily = useCallback((familyKey) => {
    setExpandedFamilies((prev) => ({ ...prev, [familyKey]: !prev[familyKey] }));
  }, []);

  // Quantity change (local state only)
  const handleQuantityChange = useCallback((productId, value) => {
    setQuantities((prev) => ({ ...prev, [productId]: value }));
  }, []);

  // Quantity commit (on blur or enter)
  const handleQuantityCommit = useCallback(async (product) => {
    if (!orderId || readOnly) return;
    const pid = product.id || product.idarticulo;
    const rawQty = quantities[pid];
    const qty = parseInt(rawQty, 10) || 0;
    const prevQty = prevQuantities[pid] || 0;

    // No change
    if (qty === prevQty) return;

    const precio = parseFloat(product.effective_price) || parseFloat(product.client_price) || parseFloat(product.pvp) || parseFloat(product.precio) || 0;
    const iva = product.iva || product.tipo_iva || 3;

    try {
      const res = await fetch(`${API_URL}/orders/${orderId}/lineas`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          idarticulo: pid,
          cantidad: qty,
          precio: precio,
          iva: iva
        })
      });
      if (res.ok) {
        // Sincronizar YA el estado local (fetchOrderLines es async y llegaba
        // tarde). Sin esto: al eliminar (qty 0) prevQuantities conservaba la
        // cantidad vieja y quantities quedaba en "0"; al re-meter la MISMA
        // cantidad, el guard `qty === prevQty` cortaba el POST y la linea no
        // volvia (input aparentemente "bloqueado").
        setPrevQuantities((prev) => {
          const next = { ...prev };
          if (qty === 0) delete next[pid]; else next[pid] = qty;
          return next;
        });
        if (qty === 0) {
          setQuantities((prev) => {
            const next = { ...prev };
            delete next[pid];
            return next;
          });
        }
        // Flash green
        setFlashId(pid);
        setTimeout(() => setFlashId(null), 600);
        // Aviso al admin si el producto entra al pedido con precio <= coste.
        // No bloquea: el producto se mete igual, solo se avisa.
        const coste = parseFloat(product.precio_coste) || 0;
        if (qty > 0 && isAdmin(permisos) && coste > 0 && precio <= coste) {
          const nombre = product.nombre || product.descripcion || `Art. ${pid}`;
          setPriceWarning(
            `Aviso: "${nombre}" se ha añadido con precio ${formatCurrency(precio)}, ` +
            `igual o por debajo del precio coste (${formatCurrency(coste)}).`
          );
        }
        // Refresh lines
        fetchOrderLines();
      }
    } catch (err) {
      console.error('Error saving quantity:', err);
    }
  }, [orderId, readOnly, quantities, prevQuantities, API_URL, token, fetchOrderLines, permisos]);

  // Price change (admin only)
  const handlePriceChange = useCallback(async (product, newPrice) => {
    if (readOnly) return;
    const pid = product.id || product.idarticulo;
    const payload = product.idprecio
      ? { idprecio: product.idprecio, precio: newPrice }
      : { idarticulo: pid, cliente: selectedClient?.id, precio: newPrice };

    try {
      const res = await fetch(`${API_URL}/orders/update-price`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        setPriceConfirmId(pid);
        setTimeout(() => setPriceConfirmId(null), 1500);
        // Aviso si el nuevo PVP queda igual o por debajo del coste.
        const coste = parseFloat(product.precio_coste) || 0;
        if (coste > 0 && newPrice <= coste) {
          const nombre = product.nombre || product.descripcion || `Art. ${pid}`;
          setPriceWarning(
            `Aviso: el precio de "${nombre}" (${formatCurrency(newPrice)}) está ` +
            `igual o por debajo del precio coste (${formatCurrency(coste)}).`
          );
        } else {
          setPriceWarning('');
        }
        // Update local product data with new price
        setFamiliesData((prev) =>
          prev.map((family) => ({
            ...family,
            products: (family.products || []).map((p) => {
              const ppid = p.id || p.idarticulo;
              if (ppid === pid) {
                return { ...p, effective_price: newPrice, client_price: newPrice, pvp: newPrice, precio: newPrice };
              }
              return p;
            })
          }))
        );
        // If product is in order, refresh lines too
        if (orderLines.some((l) => l.idarticulo === pid)) {
          fetchOrderLines();
        }
      }
    } catch (err) {
      console.error('Error updating price:', err);
    }
  }, [readOnly, API_URL, token, selectedClient, orderLines, fetchOrderLines]);

  // Totals calculation (placed before handleConfirmOrder so it can reference totals)
  const totals = useMemo(() => {
    let subtotal = 0;
    const ivaBreakdown = { 4: 0, 10: 0, 21: 0 };

    orderLines.forEach((line) => {
      const lineSubtotal = (line.precio || 0) * (line.cantidad || 0);
      subtotal += lineSubtotal;
      const ivaRate = IVA_MAP[line.iva] || 21;
      ivaBreakdown[ivaRate] = (ivaBreakdown[ivaRate] || 0) + lineSubtotal * (ivaRate / 100);
    });

    const totalIva = Object.values(ivaBreakdown).reduce((a, b) => a + b, 0);
    return { subtotal, ivaBreakdown, totalIva, total: subtotal + totalIva };
  }, [orderLines]);

  // Open pre-confirmation modal
  const handleOpenPreConfirm = useCallback(() => {
    setConfirmError('');
    setShowPreConfirmModal(true);
  }, []);

  // Confirm order flow (called from PreConfirmModal)
  const handleConfirmOrder = useCallback(async (tipo, npedidoc) => {
    if (!orderId) return;
    setConfirming(true);
    setConfirmError('');
    try {
      const body = { tipo, npedidoc: npedidoc || '' };
      const res = await fetch(`${API_URL}/orders/${orderId}/confirmar`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json();

      if (res.ok) {
        // Asegurar que el correo se envio antes de mostrar "pedido creado".
        if (data.email_enviado === false) {
          setConfirmError(
            'El pedido se confirmo, pero el correo de confirmacion NO se pudo enviar' +
            (data.email_error ? ` (${data.email_error})` : '') +
            '. Avise a administracion.'
          );
          return;
        }
        setShowPreConfirmModal(false);
        navigate('/pedidos/confirmado', {
          state: {
            orderId,
            // 'S/N' es truthy: hay que tratarlo como vacio para caer al id.
            npedido: (data.npedido && data.npedido !== 'S/N') ? data.npedido : displayNpedido,
            fecha: new Date().toLocaleDateString('es-ES'),
            total: totals.total,
            embalaje: tipo === 'embalaje',
            embalajeNeeded: data.embalaje_needed === true
          }
        });
      } else {
        setConfirmError(data.message || 'Error al confirmar el pedido');
      }
    } catch (err) {
      setConfirmError('Error al confirmar el pedido');
    } finally {
      setConfirming(false);
    }
  }, [orderId, API_URL, token, navigate, totals, displayNpedido]);

  // Save draft
  const saveDraft = useCallback(async () => {
    if (!orderId) return;
    setSaving(true);
    try {
      setSuccessMsg('Borrador guardado');
      setTimeout(() => {
        setSaving(false);
        navigate('/pedidos');
      }, 800);
    } catch (err) {
      console.error(err);
      setSaving(false);
    }
  }, [orderId, navigate]);

  // Client change handler
  const handleClientChange = useCallback((client) => {
    if (isEditingExisting) return; // Can't change client on existing orders
    if (!client) {
      setSelectedClient(null);
      setOrderId(null);
      setOrderLines([]);
      setFamiliesData([]);
      setQuantities({});
      setPrevQuantities({});
      setSearchTerm('');
    } else {
      setSelectedClient(client);
    }
  }, [isEditingExisting]);

  // Total product count
  const totalProductCount = useMemo(() => {
    return familiesData.reduce((acc, f) => acc + (f.products || []).length, 0);
  }, [familiesData]);

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* ── Header Bar ── */}
      <div className="flex-shrink-0 bg-white shadow-sm border-b border-gray-200 px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full" style={{ backgroundColor: PRIMARY_LIGHT }}>
              <ShoppingCart size={20} style={{ color: PRIMARY }} />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                {isEditingExisting ? (readOnly ? 'Ver Pedido' : 'Editar Pedido') : 'Nuevo Pedido'}
                {orderId && (
                  <span className="text-sm font-normal text-gray-400">#{displayNpedido}</span>
                )}
              </h1>
              {selectedClient && (
                <p className="text-sm text-gray-500">{selectedClient.razon_social || selectedClient.alias}</p>
              )}
            </div>
          </div>
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <ArrowLeft size={16} /> Volver
          </button>
        </div>
      </div>

      {/* ── Error Banner ── */}
      {error && (
        <div className="flex-shrink-0 mx-6 mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
          <AlertTriangle size={16} />
          {error}
          <button onClick={() => setError('')} className="ml-auto text-red-400 hover:text-red-600">
            <X size={16} />
          </button>
        </div>
      )}

      {/* ── Success Banner ── */}
      {successMsg && (
        <div className="flex-shrink-0 mx-6 mt-3 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-700 text-sm">
          <Check size={16} />
          {successMsg}
        </div>
      )}

      {/* ── Price Warning Banner (admin: precio <= coste) ── */}
      {priceWarning && (
        <div className="flex-shrink-0 mx-6 mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2 text-amber-800 text-sm font-medium">
          <AlertTriangle size={16} />
          {priceWarning}
          <button onClick={() => setPriceWarning('')} className="ml-auto text-amber-400 hover:text-amber-600">
            <X size={16} />
          </button>
        </div>
      )}

      {/* ── Read-only Banner ── */}
      {readOnly && urlReadOnly && (
        <div className="flex-shrink-0 mx-6 mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2 text-blue-700 text-sm font-medium">
          <Eye size={16} />
          Visualizaci&oacute;n de pedido #{orderNpedido || orderId}
        </div>
      )}

      {/* ── Non-editable order Banner (confirmed/shipped but not explicitly readonly) ── */}
      {readOnly && !urlReadOnly && isEditingExisting && (
        <div className="flex-shrink-0 mx-6 mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center gap-2 text-yellow-700 text-sm font-medium">
          <AlertTriangle size={16} />
          Este pedido est&aacute; confirmado y no se puede editar
        </div>
      )}

      {/* ── Admin editando un pedido confirmado ──
          El admin SI puede modificar lineas de un pedido confirmado. Se avisa
          de que esta tocando un pedido ya confirmado y que los totales se
          recalculan, pero NO se reenvia ningun correo automaticamente. */}
      {!readOnly && !urlReadOnly && isEditingExisting && orderEstado !== null && orderEstado !== 1 && isAdmin(permisos) && (
        <div className="flex-shrink-0 mx-6 mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2 text-amber-800 text-sm font-medium">
          <AlertTriangle size={16} />
          Est&aacute; editando un pedido CONFIRMADO. Los totales se recalculan autom&aacute;ticamente; no se reenv&iacute;a ning&uacute;n correo de confirmaci&oacute;n.
        </div>
      )}

      {/* ── Loading existing order ── */}
      {loadingOrder && (
        <div className="flex-shrink-0 mx-6 mt-3 p-3 bg-gray-50 border border-gray-200 rounded-lg flex items-center gap-2 text-gray-600 text-sm">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2" style={{ borderColor: PRIMARY }} />
          Cargando pedido...
        </div>
      )}

      {/* ── Client Selector (only for admin/operator/commercial roles) ──
          Si el usuario es un CLIENTE (rol 5), no tiene sentido un selector
          de búsqueda porque ya está operando sobre su propia clínica (o
          la del selector global del Header si es clínica matriz). */}
      {!selectedClient && !isEditingExisting && !isClient(permisos) && (
        <div className="flex-shrink-0 px-6 pt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Seleccionar Cliente</label>
          <ClientSearchDropdown
            selectedClient={selectedClient}
            onSelect={handleClientChange}
            token={token}
            API_URL={API_URL}
            showId={isAdmin(permisos)}
          />
        </div>
      )}

      {/* ── Body (two panels) ── */}
      <div className="flex-1 flex overflow-hidden min-h-0 p-4 gap-4">

        {/* ── Left Panel: Product Catalog by Family ── */}
        <div className="w-[58%] flex flex-col bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          {/* Client badge + search */}
          <div className="flex-shrink-0 p-4 border-b border-gray-200 space-y-3">
            {selectedClient && (
              isEditingExisting ? (
                <div className="flex items-center gap-3 px-4 py-2 rounded-lg border" style={{ borderColor: PRIMARY_BORDER, backgroundColor: PRIMARY_LIGHT }}>
                  <Package size={16} style={{ color: PRIMARY }} />
                  <span className="text-sm font-medium text-gray-800">
                    {selectedClient.razon_social || selectedClient.alias}
                  </span>
                  {isAdmin(permisos) && <span className="text-xs text-gray-500">#{selectedClient.id}</span>}
                </div>
              ) : (
                <ClientSearchDropdown
                  selectedClient={selectedClient}
                  onSelect={handleClientChange}
                  token={token}
                  API_URL={API_URL}
                  showId={isAdmin(permisos)}
                />
              )
            )}
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={isAdmin(permisos) ? "Buscar productos por nombre o referencia..." : "Filtrar productos por nombre o referencia..."}
                disabled={!selectedClient}
                className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 disabled:bg-gray-100 disabled:text-gray-400"
                style={{ '--tw-ring-color': PRIMARY }}
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X size={16} />
                </button>
              )}
              {productsLoading && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2" style={{ borderColor: PRIMARY }} />
                </div>
              )}
            </div>
            {/* #4: importar pedido desde Excel (admin, con pedido activo) */}
            {isAdmin(permisos) && orderId && (
              <div>
                <input ref={importFileRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleImportExcel} className="hidden" />
                <button
                  onClick={() => importFileRef.current && importFileRef.current.click()}
                  disabled={importing}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors disabled:opacity-50 hover:bg-gray-50"
                  style={{ borderColor: PRIMARY, color: PRIMARY }}
                  title="El Excel debe tener 2 columnas: codigo de producto y cantidad"
                >
                  <Upload size={15} />
                  {importing ? 'Importando...' : 'Importar pedido desde Excel'}
                </button>
              </div>
            )}
            {selectedClient && totalProductCount > 0 && (
              <div className="text-xs text-gray-500">
                {searchTerm
                  ? `${sortedFamilies.reduce((acc, f) => acc + f.products.length, 0)} de ${totalProductCount} productos`
                  : `${totalProductCount} productos en ${familiesData.length} familias`
                }
              </div>
            )}
          </div>

          {/* Products by Family */}
          <div className="flex-1 overflow-y-auto min-h-0">
            {!selectedClient ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-400">
                <Package size={48} className="mb-3 text-gray-300" />
                <p className="text-sm">Selecciona un cliente para ver los productos</p>
              </div>
            ) : productsLoading ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-400">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 mb-3" style={{ borderColor: PRIMARY }} />
                <p className="text-sm">Cargando productos...</p>
              </div>
            ) : sortedFamilies.length === 0 && isAdmin(permisos) && (!searchTerm || searchTerm.trim().length < 2) ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-400">
                <Search size={48} className="mb-3 text-gray-300" />
                <p className="text-sm">Busca productos por nombre o referencia</p>
                <p className="text-xs mt-1">Escribe al menos 2 caracteres para buscar</p>
              </div>
            ) : sortedFamilies.length === 0 && searchTerm ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-400">
                <Search size={48} className="mb-3 text-gray-300" />
                <p className="text-sm">No se encontraron productos para "{searchTerm}"</p>
              </div>
            ) : sortedFamilies.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-400">
                <Package size={48} className="mb-3 text-gray-300" />
                <p className="text-sm">No hay productos disponibles para este cliente</p>
              </div>
            ) : isClient(permisos) ? (
              // Cliente: lista plana, SIN familias (peticion: quitar las familias
              // de los pedidos de clientes). Se aplanan todos los productos.
              <FamilySection
                key="all"
                family=""
                hideHeader
                products={sortedFamilies.flatMap((f) => f.products || [])}
                expanded
                onToggle={() => {}}
                permisos={permisos}
                quantities={quantities}
                onQuantityChange={handleQuantityChange}
                onQuantityCommit={handleQuantityCommit}
                onPriceChange={handlePriceChange}
                flashId={flashId}
                priceConfirmId={priceConfirmId}
                readOnly={readOnly}
                productImages={productImages}
                onImageClick={setSelectedImage}
                imageBaseUrl={imageBaseUrl}
              />
            ) : (
              sortedFamilies.map((family) => {
                const familyKey = family.id || family.nombre || 'SIN FAMILIA';
                return (
                  <FamilySection
                    key={familyKey}
                    family={family.nombre || 'SIN FAMILIA'}
                    products={family.products || []}
                    expanded={expandedFamilies[familyKey] !== false}
                    onToggle={() => toggleFamily(familyKey)}
                    permisos={permisos}
                    quantities={quantities}
                    onQuantityChange={handleQuantityChange}
                    onQuantityCommit={handleQuantityCommit}
                    onPriceChange={handlePriceChange}
                    flashId={flashId}
                    priceConfirmId={priceConfirmId}
                    readOnly={readOnly}
                    productImages={productImages}
                    onImageClick={setSelectedImage}
                    imageBaseUrl={imageBaseUrl}
                  />
                );
              })
            )}
          </div>
        </div>

        {/* ── Right Panel: Order Detail ── */}
        <div className="w-[42%] flex flex-col bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          {/* Header */}
          <div className="flex-shrink-0 px-4 py-3 text-white flex items-center justify-between" style={{ backgroundColor: PRIMARY }}>
            <h2 className="text-sm font-bold flex items-center gap-2">
              <ShoppingCart size={16} />
              Detalle del Pedido
            </h2>
            <span className="text-xs bg-white/20 px-2 py-0.5 rounded">
              {orderId ? `#${displayNpedido}` : 'Nuevo'}
            </span>
          </div>

          {/* Order lines */}
          <div className="flex-1 overflow-y-auto min-h-0">
            {orderLines.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-400">
                <ShoppingCart size={40} className="mb-3 text-gray-300" />
                <p className="text-sm">No hay productos en el pedido</p>
                <p className="text-xs mt-1">Introduzca cantidades en el catálogo para agregar productos</p>
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase w-8">#</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Producto</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Precio</th>
                    <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Cant.</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Subtotal</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {orderLines.map((line, idx) => {
                    const lineKey = line.id || line.idarticulo;
                    const nombreCompleto = line.nombre || line.descripcion || `Art. ${line.idarticulo}`;
                    const referencia = line.raiz || line.referencia || '';
                    const expanded = !!expandedLines[lineKey];
                    return (
                    <tr key={lineKey} className="hover:bg-gray-50 transition-colors align-top">
                      <td className="px-2 py-2 text-center text-xs text-gray-400">{idx + 1}</td>
                      <td className="px-3 py-2">
                        {referencia && (
                          <div className="text-[11px] font-mono text-gray-400 leading-tight">Ref. {referencia}</div>
                        )}
                        <div
                          className={`text-sm text-gray-800 ${expanded ? 'whitespace-normal break-words' : 'truncate max-w-[180px]'}`}
                          title={nombreCompleto}
                        >
                          {nombreCompleto}
                        </div>
                        {nombreCompleto.length > 26 && (
                          <button
                            type="button"
                            onClick={() => setExpandedLines((prev) => ({ ...prev, [lineKey]: !prev[lineKey] }))}
                            className="mt-0.5 text-[11px] font-medium text-blue-500 hover:text-blue-700"
                          >
                            {expanded ? 'ver menos' : 'ver más'}
                          </button>
                        )}
                      </td>
                      <td className="px-3 py-2 text-sm text-right text-gray-600">
                        {formatCurrency(line.precio || 0)}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {readOnly ? (
                          <span className="text-sm font-medium text-gray-800">{line.cantidad || 0}</span>
                        ) : (
                          <input
                            type="number"
                            min="0"
                            value={quantities[line.idarticulo] ?? line.cantidad ?? ''}
                            onChange={(e) => handleQuantityChange(line.idarticulo, e.target.value)}
                            onBlur={() => handleQuantityCommit({
                              id: line.idarticulo,
                              idarticulo: line.idarticulo,
                              nombre: line.nombre || line.descripcion,
                              effective_price: line.precio,
                              precio: line.precio,
                              iva: line.iva,
                            })}
                            onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                            className="w-16 px-2 py-1 border border-gray-300 rounded text-sm text-center focus:outline-none focus:ring-1 focus:ring-blue-400"
                          />
                        )}
                      </td>
                      <td className="px-3 py-2 text-sm text-right font-medium text-gray-800">
                        {formatCurrency((line.precio || 0) * (line.cantidad || 0))}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* Totals + Actions */}
          <div className="flex-shrink-0 border-t border-gray-200">
            {/* Totals */}
            <div className="px-4 py-3 space-y-1 bg-gray-50">
              <div className="flex justify-between text-sm text-gray-600">
                <span>Subtotal (sin IVA)</span>
                <span>{formatCurrency(totals.subtotal)}</span>
              </div>
              {Object.entries(totals.ivaBreakdown).map(([rate, amount]) =>
                amount > 0 ? (
                  <div key={rate} className="flex justify-between text-xs text-gray-500">
                    <span>IVA {rate}%</span>
                    <span>{formatCurrency(amount)}</span>
                  </div>
                ) : null
              )}
              <div className="flex justify-between text-base font-bold pt-1 border-t border-gray-200" style={{ color: PRIMARY_DARK }}>
                <span>TOTAL con IVA</span>
                <span>{formatCurrency(totals.total)}</span>
              </div>
            </div>

            {/* Action buttons */}
            <div className="px-4 py-3 flex items-center gap-2 bg-white border-t border-gray-200">
              <button
                onClick={() => setShowCommentsModal(true)}
                disabled={!orderId}
                className="relative px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 flex items-center gap-1.5 text-gray-600"
              >
                <MessageSquare size={16} /> Comentarios
                {commentCount > 0 && (
                  <span
                    className="absolute -top-1.5 -right-1.5 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center"
                    style={{ backgroundColor: PRIMARY }}
                  >
                    {commentCount}
                  </span>
                )}
              </button>
              <div className="flex-1" />
              {!readOnly && !editingConfirmed && (
                <button
                  onClick={saveDraft}
                  disabled={!orderId || saving}
                  className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 flex items-center gap-1.5"
                  style={{ borderColor: PRIMARY, color: PRIMARY }}
                >
                  <Save size={16} /> Guardar Borrador
                </button>
              )}
              {!readOnly && !editingConfirmed && (
                <button
                  onClick={handleOpenPreConfirm}
                  disabled={!orderId || orderLines.length === 0 || confirming}
                  className="px-4 py-2 text-sm text-white rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1.5 font-medium"
                  style={{ backgroundColor: PRIMARY }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = PRIMARY_DARK}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = PRIMARY}
                >
                  {confirming ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  ) : (
                    <Check size={16} />
                  )}
                  Confirmar Pedido
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <PreConfirmModal
        isOpen={showPreConfirmModal}
        onClose={() => setShowPreConfirmModal(false)}
        orderLines={orderLines}
        familiesData={familiesData}
        onConfirm={handleConfirmOrder}
        loading={confirming}
        confirmError={confirmError}
      />
      <CommentsModal
        isOpen={showCommentsModal}
        onClose={() => setShowCommentsModal(false)}
        orderId={orderId}
        token={token}
        API_URL={API_URL}
      />
      <ImagePopup
        src={selectedImage}
        onClose={() => setSelectedImage(null)}
      />

      {/* Open Order Modal */}
      {showOpenOrderModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            <div className="px-6 py-4 border-l-4 border-yellow-500 bg-white flex items-center gap-3">
              <AlertTriangle size={24} className="text-yellow-500 flex-shrink-0" />
              <h3 className="text-lg font-bold text-gray-900">Pedido abierto existente</h3>
            </div>
            <div className="px-6 py-4">
              <p className="text-sm text-gray-600">
                Ya existe un pedido abierto (#{openOrderId}) para este cliente. No se puede crear un segundo pedido abierto.
              </p>
              <p className="text-sm text-gray-600 mt-2">
                ¿Desea continuar con el pedido existente?
              </p>
            </div>
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center gap-3 justify-end">
              <button
                onClick={() => {
                  setShowOpenOrderModal(false);
                  setOpenOrderId(null);
                  setSelectedClient(null);
                  setPendingClient(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors"
              >
                Volver
              </button>
              <button
                onClick={() => {
                  setShowOpenOrderModal(false);
                  navigate(`/pedidos/nuevo?order=${openOrderId}`);
                }}
                className="px-4 py-2 text-sm text-white rounded-lg font-medium transition-colors"
                style={{ backgroundColor: PRIMARY }}
              >
                Continuar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* #4: resultado de importar el Excel */}
      {importResult && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col">
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-base font-bold text-gray-800">Importación de Excel</h3>
              <button onClick={() => { setImportResult(null); setPrecioInputs({}); }} className="text-gray-400 hover:text-gray-600">
                <X size={18} />
              </button>
            </div>
            <div className="p-5 overflow-y-auto space-y-4">
              {importResult.error ? (
                <p className="text-sm text-red-600">{importResult.error}</p>
              ) : (
                <>
                  <p className="text-sm text-green-700 font-medium">✓ {importResult.added || 0} líneas añadidas al pedido.</p>

                  {(importResult.sinPrecio || []).length > 0 && (
                    <div>
                      <p className="text-sm font-semibold text-amber-700 mb-2">
                        {importResult.sinPrecio.length} producto(s) sin precio asignado — ponéles precio y se añaden:
                      </p>
                      <div className="space-y-2">
                        {importResult.sinPrecio.map((item) => (
                          <div key={item.idarticulo} className="flex items-center gap-2 border border-gray-200 rounded-md p-2">
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-gray-800 truncate">{item.nombre}</p>
                              <p className="text-[11px] text-gray-400">{item.codigo} · x{item.cantidad}</p>
                            </div>
                            <input
                              type="number"
                              step="any"
                              min="0"
                              value={precioInputs[item.idarticulo] || ''}
                              onChange={(e) => setPrecioInputs((p) => ({ ...p, [item.idarticulo]: e.target.value }))}
                              placeholder="Precio €"
                              className="w-24 px-2 py-1 border border-gray-300 rounded text-sm text-right focus:outline-none focus:ring-1"
                              style={{ '--tw-ring-color': PRIMARY }}
                            />
                            <button
                              onClick={() => assignAndAddFromImport(item, precioInputs[item.idarticulo])}
                              disabled={!precioInputs[item.idarticulo]}
                              className="px-3 py-1 text-xs font-medium rounded text-white disabled:opacity-50 whitespace-nowrap"
                              style={{ backgroundColor: '#16a34a' }}
                            >
                              Asignar y añadir
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {(importResult.noEncontrado || []).length > 0 && (
                    <div>
                      <p className="text-sm font-semibold text-red-600 mb-1">
                        {importResult.noEncontrado.length} línea(s) no importadas:
                      </p>
                      <ul className="text-xs text-gray-600 list-disc pl-5 space-y-0.5">
                        {importResult.noEncontrado.map((n, i) => (
                          <li key={i}><span className="font-mono">{n.codigo}</span> — {n.reason}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="px-5 py-3 border-t border-gray-200 flex justify-end">
              <button onClick={() => { setImportResult(null); setPrecioInputs({}); }} className="px-4 py-2 text-sm font-medium rounded-md text-white" style={{ backgroundColor: PRIMARY }}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NuevoPedido;
